import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AuthenticationService } from '@services/authentication.service';
import { ToastService } from '@services/toast.service';

@Component({
  selector: 'app-login',
  imports: [
    ReactiveFormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    TranslatePipe,
  ],
  templateUrl: './login.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginComponent {
  private router = inject(Router);
  private authService = inject(AuthenticationService);
  private toastService = inject(ToastService);
  private translateService = inject(TranslateService);

  loginForm = new FormGroup({
    username: new FormControl('', [Validators.required, Validators.email]),
    password: new FormControl('', [Validators.required]),
  });

  hidePassword = true;

  readonly isLoading = signal(false);

  async onSubmit(): Promise<void> {
    if (!this.loginForm.valid) {
      return;
    }

    const { username, password } = this.loginForm.value;

    if (!username || !password) {
      return;
    }

    if (this.isLoading()) {
      return;
    }
    this.isLoading.set(true);

    try {
      await this.authService.login(username, password);
      await this.router.navigate(['/dashboard']);
    } catch (error) {
      console.error('Login error:', error);
      const errorMessage = this.translateService.instant('login.error');

      this.toastService.showError(errorMessage);
    } finally {
      this.isLoading.set(false);
    }
  }

  togglePasswordVisibility() {
    this.hidePassword = !this.hidePassword;
  }
}
